/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import type { SoundingsCollection, SourceSelection } from '@opengeoweb/shared';
import { selectorMemoizationOptions } from '@opengeoweb/store';
import { initialSoundingsState } from './reducer';
import type { SoundingsModuleState, SoundingsStore, SourceType } from './types';
import { isRegisteredPanelId } from './utils';

const emptySoundingsCollections: SoundingsCollection[] = [];

const getSoundingsStore = (
  store: SoundingsModuleState,
  panelId?: string,
): SoundingsStore => {
  const soundingsState = store?.soundings;
  if (!panelId || !isRegisteredPanelId(soundingsState, panelId)) {
    return initialSoundingsState;
  }
  const panelStore: SoundingsStore | undefined = soundingsState?.byId[panelId];
  return panelStore ?? initialSoundingsState;
};

export const getSoundingsCollections = (
  store: SoundingsModuleState,
): SoundingsCollection[] =>
  store?.soundings?.soundingsCollections || emptySoundingsCollections;

export const getSourceSelections = createSelector(
  getSoundingsStore,
  (store: SoundingsStore): Record<string, SourceSelection> => {
    return store.selectedSources;
  },
  selectorMemoizationOptions,
);

export const getLocationSelections = createSelector(
  getSoundingsStore,
  (store: SoundingsStore) => store.selectedLocations,
  selectorMemoizationOptions,
);

export const getSelectedTime = createSelector(
  getSoundingsStore,
  (store: SoundingsStore): string =>
    store.selectedTime ?? new Date().toISOString(),
  selectorMemoizationOptions,
);

export const getDiagramType = createSelector(
  getSoundingsStore,
  (store: SoundingsStore): 'SKEWT' | 'BIJVOET' | 'EMAGRAM' =>
    store.diagramType ?? 'SKEWT',
  selectorMemoizationOptions,
);

export const getShouldHideLocationMarkers = createSelector(
  getSoundingsStore,
  (store: SoundingsStore): boolean => store.shouldHideLocationMarkers ?? false,
  selectorMemoizationOptions,
);

export const getSelectedSources = createSelector(
  getSoundingsStore,
  (store: SoundingsStore): SourceType[] => {
    const sources = store.selectedSources;
    const activeSources = Object.keys(sources).reduce<SourceType[]>(
      (list, key) => {
        const value = sources[key];
        if (value.isChecked === true) {
          return list.concat({
            collectionId: key,
            url: value.url ?? '',
            instanceId: value.instanceId,
            verticalRange: value.verticalRange,
            parameterMappings: value.parameterMappings,
            windSpeedUnit: value.windSpeedUnit ?? 'ms',
            name: value.name ?? '',
          });
        }
        return list;
      },
      [],
    );
    return activeSources;
  },
  selectorMemoizationOptions,
);

export const getSelectedSourcesAmount = createSelector(
  getSelectedSources,
  (activeSources: SourceType[]): number => {
    return activeSources.length;
  },
  selectorMemoizationOptions,
);

export const getSelectedSourcesIds = createSelector(
  getSelectedSources,
  (activeSources: SourceType[]): string[] => {
    return activeSources.map((source) => source.collectionId);
  },
  selectorMemoizationOptions,
);

export const getSoundingsPreset = createSelector(
  getSoundingsStore,
  (soundingsStore: SoundingsStore): SoundingsStore => {
    const { selectedTime, selectedSources, ...rest } = soundingsStore;

    const filteredSources = Object.entries(selectedSources).reduce<
      Record<string, SourceSelection>
    >((acc, [key, value]) => {
      const { instanceId, ...sourceWithoutInstance } = value;
      return { ...acc, [key]: sourceWithoutInstance };
    }, {});

    return {
      ...rest,
      selectedSources: filteredSources,
    };
  },
  selectorMemoizationOptions,
);

const EMPTY_SOUDINGS_PANELS = { order: [], entities: {} };

export const getSoundingPanelsInOrder = createSelector(
  getSoundingsStore,
  (store: SoundingsStore) => {
    const { order = [], entities = {} } =
      store.soundingPanels || EMPTY_SOUDINGS_PANELS;
    return order.map((id) => entities[id]);
  },
  selectorMemoizationOptions,
);
